import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  CopyButton,
  Group,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconCopy, IconExternalLink } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, podatki, type Shema } from '../api/odjemalec';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { PomocIkona } from '../pomoc/PomocIkona';
import { VklopObvestil } from '../povprasevanja/VklopObvestil';

type Nastavitve = Shema['JavnaPonudbaNastavitve'];

/**
 * Nastavitve → Javna ponudba (novo v spletu): vklop objave vozil na spletu, stalna povezava do zaloge in koda za
 * vgradnjo na spletno stran podjetja, telefon za klic in e-pošta za povpraševanja (prazno = podatek podjetja), kateri
 * podatki vozil so javni. Shrani se z enim gumbom; povezavo ob prvem vklopu določi strežnik.
 */
export function JavnaPonudba() {
  const poizvedba = useQuery({
    queryKey: ['javna-ponudba'],
    queryFn: ({ signal }) => podatki(api.GET('/api/javna-ponudba', { signal })),
  });
  if (!poizvedba.data) {
    return (
      <StanjeNalaganja
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        naslov="Nastavitev javne ponudbe ni bilo mogoče naložiti"
      />
    );
  }
  return <Obrazec key={poizvedba.dataUpdatedAt} shranjeno={poizvedba.data} />;
}

function Obrazec({ shranjeno }: { shranjeno: Nastavitve }) {
  const odjemalec = useQueryClient();
  const [n, setN] = useState<Nastavitve>(shranjeno);
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const spremenjeno = JSON.stringify(n) !== JSON.stringify(shranjeno);

  const shrani = async (z: Nastavitve) => {
    setNapaka(null);
    setPolja({});
    setDela(true);
    try {
      const novo = await podatki(api.PUT('/api/javna-ponudba', { body: z }));
      odjemalec.setQueryData(['javna-ponudba'], novo);
      await odjemalec.invalidateQueries({ queryKey: ['vozilo'] });
      notifications.show({
        color: 'green',
        message: z.vklopljena && !shranjeno.vklopljena ? 'Javna ponudba je vklopljena.' : 'Nastavitve so shranjene.',
      });
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
        setNapaka('Ni shranjeno: popravite označena polja.');
      } else {
        setNapaka(`Ni shranjeno: ${sporocilo(e)}`);
      }
    } finally {
      setDela(false);
    }
  };

  const polje = (koda: string, vklopi: boolean) =>
    setN((z) => ({
      ...z,
      polja: vklopi ? [...(z.polja ?? []), koda] : (z.polja ?? []).filter((p) => p !== koda),
    }));

  return (
    <Stack gap="md">
      <Kartica naslov="Objava vozil na spletu">
        <Stack gap="sm">
          <Switch
            size="md"
            label="Objavi vozila na zalogi na spletu"
            description="Vsako vozilo na zalogi dobi javno stran s slikami, ceno in obrazcem za povpraševanje. Posamezno vozilo lahko skrijete pri vozilu."
            checked={!!n.vklopljena}
            onChange={(e) => {
              const v = e.currentTarget.checked;
              setN((z) => ({ ...z, vklopljena: v }));
            }}
          />
          {!shranjeno.vklopljena && n.vklopljena && (
            <Text size="sm" c="dimmed">
              Po shranjevanju dobite stalno povezavo do zaloge, ki jo lahko dodate na svojo spletno stran.
            </Text>
          )}
        </Stack>
      </Kartica>

      {shranjeno.povezava && (
        <Kartica naslov="Povezava do zaloge">
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              Povezava se ne spreminja – uporabite jo na svoji spletni strani, v oglasih ali na družbenih omrežjih.
              {!shranjeno.vklopljena && ' Ko je objava izklopljena, stran ni dostopna.'}
            </Text>
            <Group gap={6} wrap="nowrap">
              <TextInput value={shranjeno.povezava} readOnly aria-label="Povezava do zaloge" style={{ flex: 1 }} />
              <Kopiraj vrednost={shranjeno.povezava} oznaka="Kopiraj povezavo" />
              <ActionIcon
                component="a"
                href={shranjeno.povezava}
                target="_blank"
                rel="noopener"
                variant="subtle"
                size="lg"
                aria-label="Odpri zalogo"
              >
                <IconExternalLink size={18} />
              </ActionIcon>
            </Group>
            {shranjeno.kodaZaVgradnjo && (
              <>
                <Text size="sm" fw={600} mt="xs">
                  Vgradnja na vašo spletno stran
                </Text>
                <Text size="sm" c="dimmed" mt={-6}>
                  Kodo prilepite na stran, kjer naj bo prikazana zaloga (npr. v blok »HTML« v WordPressu).
                </Text>
                <Group gap={6} wrap="nowrap" align="flex-start">
                  <Textarea
                    value={shranjeno.kodaZaVgradnjo}
                    readOnly
                    autosize
                    minRows={2}
                    aria-label="Koda za vgradnjo"
                    styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
                    style={{ flex: 1 }}
                  />
                  <Kopiraj vrednost={shranjeno.kodaZaVgradnjo} oznaka="Kopiraj kodo" />
                </Group>
              </>
            )}
          </Stack>
        </Kartica>
      )}

      <Kartica naslov="Kontakt za kupce">
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
          <TextInput
            label="Telefon za klic"
            description="Prikazan na javni strani in oglasu."
            placeholder={shranjeno.telefonPodjetja || '041 123 456'}
            value={n.telefon ?? ''}
            onChange={(e) => {
              const v = e.currentTarget.value;
              setN((z) => ({ ...z, telefon: v }));
            }}
            error={polja.telefon}
            inputMode="tel"
          />
          <TextInput
            label="E-pošta za povpraševanja"
            description="Sem pošljemo obvestilo o vsakem povpraševanju."
            placeholder={shranjeno.epostaPodjetja || 'prodaja@podjetje.si'}
            value={n.eposta ?? ''}
            onChange={(e) => {
              const v = e.currentTarget.value;
              setN((z) => ({ ...z, eposta: v }));
            }}
            error={polja.eposta}
            type="email"
          />
        </SimpleGrid>
        {(shranjeno.telefonPodjetja || shranjeno.epostaPodjetja) && (
          <Text size="xs" c="dimmed" mt="xs">
            Prazno polje pomeni podatek podjetja iz »Naslov in kontakt«.
          </Text>
        )}
      </Kartica>

      <Kartica naslov="Javni podatki vozil">
        <Text size="sm" c="dimmed" mb="sm">
          Znamka, tip, slike in opis so vedno prikazani. Izberite, kaj še vidijo kupci.
        </Text>
        <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="xs">
          {(shranjeno.moznaPolja ?? []).map((p) => (
            <Checkbox
              key={p.koda}
              label={p.naziv}
              checked={(n.polja ?? []).includes(p.koda ?? '')}
              onChange={(e) => polje(p.koda ?? '', e.currentTarget.checked)}
            />
          ))}
        </SimpleGrid>
        {polja.polja && (
          <Text size="sm" c="red" mt="xs">
            {polja.polja}
          </Text>
        )}
      </Kartica>

      <Kartica naslov="Obvestila o povpraševanjih v tem brskalniku" dejanja={<PomocIkona clanek="povprasevanja" />}>
        <Text size="sm" c="dimmed" mb="sm">
          Takoj ko kupec pošlje povpraševanje, prejmete obvestilo – tudi ko aplikacija ni odprta. Vklopite jih na vsaki
          napravi, kjer jih želite.
        </Text>
        <VklopObvestil />
      </Kartica>

      {napaka && (
        <Alert color="red" variant="light" role="alert">
          {napaka}
        </Alert>
      )}
      <Group justify="flex-end">
        <Button onClick={() => void shrani(n)} loading={dela} disabled={!spremenjeno}>
          Shrani
        </Button>
      </Group>
    </Stack>
  );
}

function Kopiraj({ vrednost, oznaka }: { vrednost: string; oznaka: string }) {
  return (
    <CopyButton value={vrednost}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? 'Kopirano' : oznaka}>
          <ActionIcon variant="light" size="lg" onClick={copy} aria-label={oznaka}>
            {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
          </ActionIcon>
        </Tooltip>
      )}
    </CopyButton>
  );
}
